import * as React from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, AlertTriangle, ArrowLeft, Printer } from 'lucide-react';

import { api, ErroApi } from '@/lib/api';
import { useAuth } from '@/features/auth/contexto';
import { chaveDoEstado, formatoDaUrl, type FormatoImpressao } from '@/features/marcacao/formato';
import type { CampoConferencia, RespostaMarcacao } from '@/features/marcacao/tipos';
import { FolhaMarcacao } from '@/components/marcacao/folha-marcacao';
import { CupomMarcacao } from '@/components/marcacao/cupom-marcacao';
import { Botao } from '@/components/ui/button';
import { Esqueleto } from '@/components/ui/skeleton';

const ROTULOS_CONFERENCIA: Record<CampoConferencia, string> = {
  cpf: 'CPF',
  cns: 'CNS',
  nome: 'nome',
  dataNascimento: 'data de nascimento',
  nomeMae: 'nome da mãe',
};

const FORMATOS: { valor: FormatoImpressao; rotulo: string }[] = [
  { valor: 'a4', rotulo: 'Folha A4' },
  { valor: 'termica', rotulo: 'Térmica 80 mm' },
];

/**
 * Comprovante de marcação: o cupom reduzido para impressora térmica de 80 mm,
 * que é o padrão, ou a folha A4 no layout da impressão do eSUS Regulação.
 *
 * Os dados vêm do eSUS na hora em que a tela abre, e não do que o KAVI gravou
 * ao agendar: uma remarcação feita lá depois precisa sair no papel.
 *
 * Abre o diálogo de impressão sozinho quando os dados chegam — a não ser que
 * a identificação do paciente divirja, caso em que quem imprime precisa ler o
 * aviso antes.
 */
export function PaginaMarcacao() {
  const { id } = useParams<{ id: string }>();
  const { usuario } = useAuth();
  const [parametros, setParametros] = useSearchParams();
  const formato = formatoDaUrl(parametros.get('formato'));
  const local = useLocation();
  const chaveConfirmacao = chaveDoEstado(local.state);

  // `replace`: trocar o formato não é navegação, e o "voltar" do navegador
  // deve levar à solicitação, não ao formato anterior.
  const escolherFormato = (novo: FormatoImpressao) => {
    // O estado leva a chave de confirmação junto.
    setParametros({ formato: novo }, { replace: true, state: local.state });
  };

  const { data, isPending, error } = useQuery({
    queryKey: ['marcacao', id],
    queryFn: () => api.get<RespostaMarcacao>(`/solicitacoes/${id}/marcacao`),
    enabled: Boolean(id),
    // Cada abertura é uma consulta auditada ao eSUS: nada de repetir sozinha.
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const impresso = React.useRef(false);
  React.useEffect(() => {
    if (!data || data.divergencias.length > 0 || impresso.current) return;
    impresso.current = true;
    // Deixa o navegador pintar a folha antes de abrir o diálogo.
    const espera = window.setTimeout(() => window.print(), 300);
    return () => window.clearTimeout(espera);
  }, [data]);

  const voltar = (
    <Botao variante="fantasma" tamanho="sm" asChild className="-ml-3">
      <Link to={`/solicitacoes/${id}`}>
        <ArrowLeft aria-hidden="true" />
        Voltar à solicitação
      </Link>
    </Botao>
  );

  if (isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Esqueleto className="h-8 w-full max-w-64" />
        <Esqueleto className="h-[60vh] w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-4 py-16 text-center">
        <AlertCircle className="text-silenciado-texto size-12" aria-hidden="true" />
        <h1 className="text-lg font-semibold">Não foi possível montar a marcação</h1>
        <p className="text-silenciado-texto max-w-prose text-sm">
          {error instanceof ErroApi ? error.mensagemUsuario : 'Tente novamente em instantes.'}
        </p>
        {voltar}
      </div>
    );
  }

  const { protocolo, marcacao, divergencias } = data;
  const impressoPor = usuario?.nome ?? null;

  return (
    <div className="flex flex-col gap-4">
      <div className="sem-impressao flex flex-wrap items-center gap-2">
        {voltar}
        <p className="text-silenciado-texto text-sm">
          Dados consultados agora no eSUS Regulação ·{' '}
          {/* A chave vem do modal, e não da URL: recarregar a tela a perde, e
              quem imprime precisa ver o que vai sair no papel. */}
          {chaveConfirmacao ? (
            <>
              Chave de confirmação{' '}
              <strong className="text-fundo-texto font-mono">{chaveConfirmacao}</strong>
            </>
          ) : (
            'sem chave de confirmação'
          )}
        </p>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <div role="group" aria-label="Formato de impressão" className="flex">
            {FORMATOS.map((opcao, i) => (
              <Botao
                key={opcao.valor}
                variante={formato === opcao.valor ? 'primario' : 'contorno'}
                tamanho="sm"
                aria-pressed={formato === opcao.valor}
                className={i === 0 ? 'rounded-r-none' : '-ml-px rounded-l-none'}
                onClick={() => escolherFormato(opcao.valor)}
              >
                {opcao.rotulo}
              </Botao>
            ))}
          </div>
          <Botao onClick={() => window.print()}>
            <Printer aria-hidden="true" />
            Imprimir
          </Botao>
        </div>
      </div>

      {divergencias.length > 0 && (
        <div
          className="sem-impressao bg-status-devolvido-fundo text-status-devolvido-texto border-status-devolvido-borda rounded-lg border p-4 text-sm"
          role="alert"
        >
          <p className="flex items-center gap-2 font-semibold">
            <AlertTriangle className="size-4.5 shrink-0" aria-hidden="true" />O paciente no eSUS não
            confere com a solicitação
          </p>
          <p className="mt-1.5">
            Diverge: {divergencias.map((campo) => ROTULOS_CONFERENCIA[campo]).join(', ')}. Confira o
            identificador do eSUS antes de entregar esta marcação ao paciente.
          </p>
        </div>
      )}

      {formato === 'termica' ? (
        <CupomMarcacao
          protocolo={protocolo}
          marcacao={marcacao}
          chaveConfirmacao={chaveConfirmacao}
          impressoPor={impressoPor}
        />
      ) : (
        <FolhaMarcacao
          marcacao={marcacao}
          chaveConfirmacao={chaveConfirmacao}
          impressoPor={impressoPor}
        />
      )}
    </div>
  );
}
